import React, {Fragment, useEffect, useState} from "react";
import {useStateContext} from "../contexts/ContextProvider";
import {cancelBooking, findWebshopOrderItemsWithPagination, updateUser} from "../api/apiClient";
import {useActiveBookings} from "../hooks/useActiveBookings";
import {usePastBookings} from "../hooks/usePastBookings";
import {useActiveProducts} from "../hooks/useActiveProducts";
import {useWebshopOrders} from "../hooks/useWebshopOrders";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import {isShopEnabled} from "../utility/featureFlags";

const AccountPage = () => {
    const {user, token, setUser} = useStateContext();
    const [activeTab, setActiveTab] = useState(null);
    const [activeBookingRefreshCount, setActiveBookingRefreshCount] = useState(0);
    const [bookingTab, setBookingTab] = useState("active"); // Manage bookings view (active vs. past)
    const [activeBookingPage, setActiveBookingPage] = useState(1);
    const [pastBookingPage, setPastBookingPage] = useState(1);
    const [activeProductPage, setActiveProductPage] = useState(1);
    const [webshopOrderPage, setWebshopOrderPage] = useState(1);
    const navigate = useNavigate();

    const [activeBookings, setActiveBookings] = useState([]);
    const [pastBookings, setPastBookings] = useState([]);
    const {activeBookings : newActiveBookings, activeBookingsPagination} = useActiveBookings(activeBookingPage, activeBookingRefreshCount);
    const {pastBookings : newPastBookings, pastBookingsPagination} = usePastBookings(pastBookingPage, 0);

    const {activeProducts, activeProductsPagination} = useActiveProducts(activeProductPage, 0);

    const [webshopOrders, setWebshopOrders] = useState([]);
    const {webshopOrders : newWebshopOrders, webshopOrdersPagination} = useWebshopOrders(webshopOrderPage, 0);

    const [isConfirmVisible, setIsConfirmVisible] = useState(false); // For confirmation overlay
    const [bookingToCancel, setBookingToCancel] = useState(null); // Booking details for cancellation
    const [cancelling, setCancelling] = useState(false);

    // Webshop order detail overlay
    const [isOrderModalVisible, setIsOrderModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderItems, setOrderItems] = useState([]);
    const [orderItemsPagination, setOrderItemsPagination] = useState(null);
    const [orderItemsPage, setOrderItemsPage] = useState(1);
    const [loadingOrderItems, setLoadingOrderItems] = useState(false);

    // Local state for form fields
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [zip, setZip] = useState('');
    const [birthDate, setBirthDate] = useState('');

    useEffect(() => {
        if (!token) {
            navigate("/login");
        }
    }, [navigate, token]);

    const mergeBookings = (existing, incoming) => {
        const existingIds = new Set(existing.map((b) => b.bookingUuid));
        return [...existing, ...incoming.filter((b) => !existingIds.has(b.bookingUuid))];
    };

    const mergeWebshopOrders = (existing, incoming) => {
        const existingIds = new Set(existing.map((o) => o.orderUuid));
        return [...existing, ...incoming.filter((o) => !existingIds.has(o.orderUuid))];
    };

    // Handle updates to active bookings
    useEffect(() => {
        if (activeBookingPage === 1) {
            setActiveBookings(newActiveBookings);
        }
        else if (newActiveBookings.length > 0) {
            setActiveBookings((prev) => mergeBookings(prev, newActiveBookings));
        }
    }, [newActiveBookings, activeBookingPage]);

    // Handle updates to past bookings
    useEffect(() => {
        if (pastBookingPage === 1) {
            setPastBookings(newPastBookings);
        }
        else if (newPastBookings.length > 0) {
            setPastBookings((prev) => mergeBookings(prev, newPastBookings));
        }
    }, [newPastBookings, pastBookingPage]);

    // Handle updates to webshop orders
    useEffect(() => {
        if (webshopOrderPage === 1) {
            setWebshopOrders(newWebshopOrders);
        }
        else if (newWebshopOrders.length > 0) {
            setWebshopOrders((prev) => mergeWebshopOrders(prev, newWebshopOrders));
        }
    }, [newWebshopOrders, webshopOrderPage]);

    // Fetch items for the selected webshop order (opened via the detail overlay)
    useEffect(() => {
        if (!selectedOrder) {
            return;
        }

        const abortController = new AbortController();
        setLoadingOrderItems(true);

        findWebshopOrderItemsWithPagination(selectedOrder.orderUuid, orderItemsPage, abortController.signal)
            .then((data) => {
                const newItems = data.customerItems;
                setOrderItems((prev) => orderItemsPage === 1 ? newItems : [...prev, ...newItems]);
                setOrderItemsPagination(data.pagination);
            })
            .catch((error) => {
                console.log(error);
                toast.error("Bestellung konnte nicht geladen werden.");
                setIsOrderModalVisible(false);
            })
            .finally(() => {
                setLoadingOrderItems(false);
            });

        return () => {
            abortController.abort();
        };
    }, [selectedOrder, orderItemsPage]);

    // Update state when user changes
    useEffect(() => {
        if (user) {
            setFirstName(user.firstName || '');
            setLastName(user.lastName || '');
            setPhone(user.phone || '');
            setStreet(user.street || '');
            setCity(user.city || '');
            setZip(user.zip || '');
            setBirthDate(user.birthDate || '');
        }
    }, [user]);

    const toggleTab = (tabName) => {
        setActiveTab(activeTab === tabName ? null : tabName);
    };

    const handleSave = async () => {
        updateUser(
            firstName,
            lastName,
            phone,
            birthDate,
            street,
            city,
            zip,
        ).then((data) => {
            toast.success('Daten erfolgreich gespeichert!');
            setUser({
                firstName : firstName,
                lastName  : lastName,
                email     : user.email,
                birthDate : birthDate,
                phone     : phone,
                gender    : user.gender,
                street    : street,
                zip       : zip,
                city      : city,
            });
        })
            .catch((err) => {
                toast.error('Fehler beim Speichern der Daten.');
                const response = err.response;

                console.log(response);
            });
    };

    // Handle cancel booking
    const handleCancelBooking = async () => {
        if (!bookingToCancel) {
            return;
        }

        setCancelling(true);

        cancelBooking(bookingToCancel.bookingUuid)
            .then(() => {
                toast.success('Buchung erfolgreich storniert!');
                setIsConfirmVisible(false);
                setActiveBookingPage(1);
                setActiveBookingRefreshCount((prev) => prev + 1);
            })
            .catch((err) => {
                    toast.error('Fehler beim Stornieren der Buchung.');
                    const response = err.response;

                    console.log(response);
                }
            )
            .finally(() => {
                setCancelling(false);
            });
    };

    const showConfirmOverlay = (booking) => {
        setBookingToCancel(booking);
        setIsConfirmVisible(true);
    };

    const handleOpenOrder = (order) => {
        setSelectedOrder(order);
        setOrderItems([]);
        setOrderItemsPagination(null);
        setOrderItemsPage(1);
        setIsOrderModalVisible(true);
    };

    const closeOrderModal = () => {
        setIsOrderModalVisible(false);
        setSelectedOrder(null);
    };

    // Determine which bookings to display
    const displayedBookings = bookingTab === "active" ? activeBookings : pastBookings;

    return <Fragment>
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
            <div className="container mx-auto">
                {/* Confirmation Overlay */}
                {isConfirmVisible && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                        <div className="bg-white rounded-lg shadow-lg p-6 w-96">
                            <h2 className="text-lg font-bold text-gray-800 mb-2">Hey, {user.firstName}!</h2>
                            <p className="text-gray-700 mb-4">
                                Bist du sicher, dass du deine Buchung stornieren möchtest? <br/>
                                {
                                    bookingToCancel?.canCancelWithCreditBack
                                        ? "Deine Einheit wird zurückerstattet."
                                        : "Bei Stornierungen innerhalb von " + bookingToCancel?.cancellationCreditRefundPeriodInHours + " Stunden vor Veranstaltungsbegin erfolgt keine Rückerstattung."
                                }
                            </p>
                            <div className="text-center mb-4">
                                <p className="font-bold text-gray-900">{bookingToCancel?.eventName}</p>
                                <p className="text-sm text-gray-500">
                                    {new Date(bookingToCancel?.eventStartedAt).toLocaleDateString("de-DE", {
                                        weekday : "long",
                                        day     : "2-digit",
                                        month   : "2-digit",
                                        year    : "numeric",
                                    })}{" "}
                                    |{" "}
                                    {new Date(bookingToCancel?.eventStartedAt).toLocaleTimeString("de-DE", {
                                        hour   : "2-digit",
                                        minute : "2-digit",
                                    })}{" "}
                                    -{" "}
                                    {new Date(bookingToCancel?.eventEndedAt).toLocaleTimeString("de-DE", {
                                        hour   : "2-digit",
                                        minute : "2-digit",
                                    })}
                                </p>
                            </div>
                            <button
                                onClick={handleCancelBooking}
                                disabled={cancelling}
                                className={`w-full py-2 rounded-lg font-bold flex items-center justify-center ${
                                    cancelling
                                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                                        : "bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                                }`}
                            >
                                {cancelling && (
                                    <span className="inline-block w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                )}
                                {cancelling ? "Wird storniert..." : "BESTÄTIGE STORNIERUNG"}
                            </button>
                            <button
                                onClick={() => setIsConfirmVisible(false)}
                                disabled={cancelling}
                                className="text-pegasusPurple mt-2 underline w-full disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Abbrechen
                            </button>
                        </div>
                    </div>
                )}

                {/* Webshop Order Detail Overlay */}
                {isOrderModalVisible && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-2">
                                <h2 className="text-lg font-bold text-gray-800">
                                    Bestellung #{selectedOrder?.number}
                                </h2>
                                <button
                                    onClick={closeOrderModal}
                                    className="text-gray-500 hover:text-gray-700"
                                    aria-label="Schließen"
                                >
                                    Schließen &#10005;
                                </button>
                            </div>
                            <p className="text-sm text-gray-500 mb-4">
                                {selectedOrder && new Date(selectedOrder.createdAt).toLocaleDateString("de-DE", {
                                    weekday : "long",
                                    day     : "2-digit",
                                    month   : "long",
                                    year    : "numeric",
                                })}
                                {" · "}
                                Gesamt: {selectedOrder?.bruttoPrice.toFixed(2)} {selectedOrder?.currency.toUpperCase()}
                            </p>

                            <div className="space-y-4">
                                {orderItems.map((item) => (
                                    <div
                                        key={item.customerItemUuid}
                                        className="flex items-center border-b last:border-b-0 pb-4"
                                    >
                                        {item.itemVariant?.images?.[0]?.url && (
                                            <img
                                                src={item.itemVariant.images[0].url}
                                                alt={item.name}
                                                className="w-16 h-16 rounded-md object-cover mr-4"
                                            />
                                        )}
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-gray-800">{item.name}</h3>
                                            {item.itemVariant?.properties?.length > 0 && (
                                                <p className="text-sm text-gray-500">
                                                    {item.itemVariant.properties.map((property) => property.value).join(" · ")}
                                                </p>
                                            )}
                                            <p className="text-sm text-gray-500">Menge: {item.quantity}</p>
                                        </div>
                                        <div className="text-pegasusPurple font-bold">
                                            {item.bruttoPrice.toFixed(2)} {selectedOrder?.currency.toUpperCase()}
                                        </div>
                                    </div>
                                ))}
                                {orderItems.length === 0 && (
                                    <p className="text-gray-500">
                                        {loadingOrderItems ? "Wird geladen..." : "Keine Artikel gefunden."}
                                    </p>
                                )}
                            </div>

                            {orderItemsPagination?.totalPages > orderItemsPage && (
                                <button
                                    onClick={() => setOrderItemsPage((prev) => prev + 1)}
                                    className="text-pegasusPurple hover:underline mt-4"
                                >
                                    Mehr laden
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Layout */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Bookings Section */}
                    <section className="bg-white shadow-md rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-700">Buchungen</h2>
                            <button
                                className="text-pegasusPurple hover:underline"
                                onClick={() =>
                                    setBookingTab(bookingTab === "active" ? "past" : "active")
                                }
                            >
                                {bookingTab === "active"
                                    ? "Vergangene >"
                                    : "Nächste Termine >"}
                            </button>
                        </div>
                        <ul>
                            {displayedBookings && displayedBookings.length > 0 ? (
                                displayedBookings.map((booking) => {
                                    const isCancellable = bookingTab === "active" && !booking.eventCancelledAt;

                                    return (
                                        <li
                                            key={booking.bookingUuid}
                                            onClick={isCancellable ? () => showConfirmOverlay(booking) : undefined}
                                            className={`flex items-center border-b last:border-b-0 py-4 ${
                                                isCancellable ? "cursor-pointer hover:bg-gray-50" : ""
                                            }`}
                                        >
                                            <img
                                                src={booking.eventImage}
                                                alt={booking.eventName}
                                                className="w-16 h-16 rounded-md mr-4 object-cover"
                                            />
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-800">
                                                    {booking.eventName}
                                                </h3>
                                                <p className="text-sm text-gray-500">
                                                    {new Date(
                                                        booking.eventStartedAt
                                                    ).toLocaleDateString("de-DE", {
                                                        weekday : "long",
                                                        day     : "2-digit",
                                                        month   : "2-digit",
                                                        year    : "numeric",
                                                    })}{" "}
                                                    |{" "}
                                                    {new Date(
                                                        booking.eventStartedAt
                                                    ).toLocaleTimeString("de-DE", {
                                                        hour   : "2-digit",
                                                        minute : "2-digit",
                                                    })}{" "}
                                                    -{" "}
                                                    {new Date(
                                                        booking.eventEndedAt
                                                    ).toLocaleTimeString("de-DE", {
                                                        hour   : "2-digit",
                                                        minute : "2-digit",
                                                    })}
                                                </p>
                                                {booking.eventCancelledAt ? (
                                                    <p className="text-sm text-gray-500 font-semibold">Abgesagt</p>
                                                ) : (
                                                    isCancellable && (
                                                        <p className="text-sm text-pegasusPurple underline">
                                                            Termin stornieren
                                                        </p>
                                                    )
                                                )}
                                            </div>
                                        </li>
                                    );
                                })
                            ) : (
                                <p className="text-gray-500">
                                    Keine Buchungen verfügbar
                                </p>
                            )}
                        </ul>
                        {(bookingTab === "active" &&
                            activeBookingsPagination?.totalPages >
                            activeBookingPage) && (
                            <button
                                onClick={() => setActiveBookingPage((prev) => prev + 1)}
                                className="text-pegasusPurple hover:underline mt-4"
                            >
                                Mehr laden
                            </button>
                        )}
                        {(bookingTab === "past" &&
                            pastBookingsPagination?.totalPages >
                            pastBookingPage) && (
                            <button
                                onClick={() => setPastBookingPage((prev) => prev + 1)}
                                className="text-pegasusPurple hover:underline mt-4"
                            >
                                Mehr laden
                            </button>
                        )}
                    </section>

                    <div className="space-y-4">
                        {/* Active Products */}
                        <section className="bg-white shadow-md rounded-lg p-4">
                            <h2 className="text-xl font-bold text-gray-700 mb-4">Aktive Produkte</h2>
                            <ul>
                                {activeProducts && activeProducts.length > 0 ? (
                                    activeProducts.map((product) => (
                                        <li
                                            key={product.customerProductUuid}
                                            className="flex items-center border-b last:border-b-0 py-4"
                                        >
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-800">
                                                    {product.name}
                                                </h3>
                                                {product.validFrom && product.validTo ? (
                                                    <p className="text-sm text-gray-500">
                                                        Gültig von{" "}
                                                        {new Date(product.validFrom).toLocaleDateString("de-DE", {
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                            year: "numeric",
                                                        })}{" "}
                                                        bis{" "}
                                                        {new Date(product.validTo).toLocaleDateString("de-DE", {
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                            year: "numeric",
                                                        })}
                                                    </p>
                                                ) : (
                                                    <p className="text-sm text-gray-500">
                                                        Gültigkeitsdauer berechnet nach der ersten Buchung
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-pegasusPurple font-bold">
                                                {product.creditLeft}/{product.credit}
                                            </div>
                                        </li>
                                    ))
                                ) : (
                                    <p className="text-gray-500">Keine aktiven Produkte verfügbar</p>
                                )}
                            </ul>
                            {activeProductsPagination?.totalPages > activeProductPage && (
                                <button
                                    onClick={() => setActiveProductPage((prev) => prev + 1)}
                                    className="text-pegasusPurple hover:underline mt-4"
                                >
                                    Mehr laden
                                </button>
                            )}
                        </section>

                        {/* Webshop Orders */}
                        {isShopEnabled() && (
                            <section className="bg-white shadow-md rounded-lg p-4">
                                <h2 className="text-xl font-bold text-gray-700 mb-4">Webshop-Bestellungen</h2>
                                <ul>
                                    {webshopOrders && webshopOrders.length > 0 ? (
                                        webshopOrders.map((order) => (
                                            <li
                                                key={order.orderUuid}
                                                onClick={() => handleOpenOrder(order)}
                                                className="flex items-center justify-between border-b last:border-b-0 py-4 cursor-pointer hover:bg-gray-50"
                                            >
                                                <div>
                                                    <h3 className="font-semibold text-gray-800">
                                                        Bestellung #{order.number}
                                                    </h3>
                                                    <p className="text-sm text-gray-500">
                                                        {new Date(order.createdAt).toLocaleDateString("de-DE", {
                                                            day   : "2-digit",
                                                            month : "2-digit",
                                                            year  : "numeric",
                                                        })}
                                                    </p>
                                                </div>
                                                <div className="text-pegasusPurple font-bold">
                                                    {order.bruttoPrice.toFixed(2)} {order.currency.toUpperCase()}
                                                </div>
                                            </li>
                                        ))
                                    ) : (
                                        <p className="text-gray-500">Keine Bestellungen verfügbar</p>
                                    )}
                                </ul>
                                {webshopOrdersPagination?.totalPages > webshopOrderPage && (
                                    <button
                                        onClick={() => setWebshopOrderPage((prev) => prev + 1)}
                                        className="text-pegasusPurple hover:underline mt-4"
                                    >
                                        Mehr laden
                                    </button>
                                )}
                            </section>
                        )}
                    </div>

                    {/* Tabs */}
                    <section className="bg-white shadow-md rounded-lg p-4">
                        <h2 className="text-xl font-bold text-gray-700 mb-4">Datenübersicht</h2>

                        {/* Tab: Kontodaten */}
                        <div>
                            <button
                                className="w-full text-left font-medium text-gray-700 py-2 border-b"
                                onClick={() => toggleTab("Kontodaten")}
                            >
                                Kontodaten
                            </button>
                            {activeTab === "Kontodaten" && (
                                <div className="p-4 space-y-4">
                                    <label className="block">
                                        <span className="text-sm text-gray-600">Email</span>
                                        <input
                                            type="email"
                                            className="w-full border border-gray-300 rounded-md p-2 mt-1 bg-gray-100 cursor-not-allowed"
                                            value={user?.email || ""}
                                            disabled
                                        />
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Tab: Persönliche Daten */}
                        <div>
                            <button
                                className="w-full text-left font-medium text-gray-700 py-2 border-b"
                                onClick={() => toggleTab("Persönliche Daten")}
                            >
                                Persönliche Daten
                            </button>
                            {activeTab === "Persönliche Daten" && (
                                <div className="p-4 space-y-4">
                                    <label className="block">
                                        <span className="text-sm text-gray-600">Vorname</span>
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded-md p-2 mt-1"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            placeholder="Vorname"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-sm text-gray-600">Nachname</span>
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded-md p-2 mt-1"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            placeholder="Nachname"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-sm text-gray-600">Geburtstag</span>
                                        <input
                                            type="date"
                                            className="w-full border border-gray-300 rounded-md p-2 mt-1"
                                            value={birthDate}
                                            onChange={(e) => setBirthDate(e.target.value)}
                                        />
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Tab: Kontaktdaten */}
                        <div>
                            <button
                                className="w-full text-left font-medium text-gray-700 py-2 border-b"
                                onClick={() => toggleTab("Kontaktdaten")}
                            >
                                Kontaktdaten
                            </button>
                            {activeTab === "Kontaktdaten" && (
                                <div className="p-4 space-y-4">
                                    <label className="block">
                                        <span className="text-sm text-gray-600">Telefonnummer</span>
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded-md p-2 mt-1"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder="Telefonnummer"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-sm text-gray-600">Straße & Hausnummer</span>
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded-md p-2 mt-1"
                                            value={street}
                                            onChange={(e) => setStreet(e.target.value)}
                                            placeholder="Straße & Hausnummer"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-sm text-gray-600">Postleitzahl</span>
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded-md p-2 mt-1"
                                            value={zip}
                                            onChange={(e) => setZip(e.target.value)}
                                            placeholder="Postleitzahl"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-sm text-gray-600">Stadt</span>
                                        <input
                                            type="text"
                                            className="w-full border border-gray-300 rounded-md p-2 mt-1"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            placeholder="Stadt"
                                        />
                                    </label>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={handleSave}
                            className="w-full bg-pegasusPurple text-white py-2 px-4 rounded-md hover:bg-pegasusPurpleHover mt-4"
                        >
                            SPEICHERN
                        </button>
                    </section>
                </div>
            </div>
        </div>
    </Fragment>
}

export default AccountPage;